import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Apply() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    age: "",
    program: "womens",
    has_income: "yes",
    move_in_timeline: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        age: form.age ? parseInt(form.age, 10) : null,
        program: form.program,
        has_income: form.has_income === "yes",
        move_in_timeline: form.move_in_timeline || null,
        message: form.message || null,
      };
      await axios.post(`${API}/applications`, payload);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="apply-page">
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#1a0a2e] via-[#240a3d] to-[#2e0e54]" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 pt-20 pb-12 text-center">
          <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">Apply</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#f7f2e7]">
            Start your <span className="gold-text">application</span>
          </h1>
          <p className="text-[#dcd0b3] mt-5 max-w-2xl mx-auto">
            Tell us a little about yourself. Every application is reviewed
            personally, and we'll reach out with next steps.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          {success ? (
            <div data-testid="apply-success" className="card-purple p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#d4af37]" size={52} />
              <h2 className="font-serif text-3xl text-[#f7f2e7] mt-5">Application received</h2>
              <p className="text-[#dcd0b3] mt-3">
                Thank you, {form.full_name.split(" ")[0]}. We'll be in touch within
                1–2 business days at <span className="text-[#f0d479]">{form.email}</span>.
              </p>
              <p className="text-[#b395d6] text-sm mt-2">
                Need to talk sooner? Call us at <a className="underline" href="tel:7064318996">706-431-8996</a>.
              </p>
              <button
                data-testid="apply-reset-btn"
                onClick={() => {
                  setSuccess(false);
                  setForm({
                    full_name: "", email: "", phone: "", age: "",
                    program: "womens", has_income: "yes", move_in_timeline: "", message: "",
                  });
                }}
                className="btn-outline-gold mt-7"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="apply-form"
              className="card-purple p-7 md:p-10 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input
                    required
                    data-testid="apply-input-name"
                    className="input-base"
                    placeholder="Jane Doe"
                    value={form.full_name}
                    onChange={update("full_name")}
                  />
                </Field>
                <Field label="Age">
                  <input
                    type="number"
                    min="18"
                    data-testid="apply-input-age"
                    className="input-base"
                    placeholder="30+"
                    value={form.age}
                    onChange={update("age")}
                  />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Email *">
                  <input
                    type="email"
                    required
                    data-testid="apply-input-email"
                    className="input-base"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </Field>
                <Field label="Phone *">
                  <input
                    required
                    data-testid="apply-input-phone"
                    className="input-base"
                    placeholder="(706) 431-8996"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </Field>
              </div>

              <Field label="Which Haven? *">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: "womens", l: "Women's Haven" },
                    { v: "mens", l: "Men's Haven" },
                  ].map((o) => (
                    <button
                      type="button"
                      key={o.v}
                      data-testid={`apply-program-${o.v}`}
                      onClick={() => setForm((f) => ({ ...f, program: o.v }))}
                      className={`py-3 rounded-xl border transition-all ${
                        form.program === o.v
                          ? "border-[#d4af37] bg-[rgba(212,175,55,0.12)] text-[#f0d479]"
                          : "border-[rgba(212,175,55,0.25)] text-[#dcd0b3] hover:border-[#d4af37]"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Do you have verified guaranteed income? *">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: "yes", l: "Yes" },
                    { v: "no", l: "Not yet" },
                  ].map((o) => (
                    <button
                      type="button"
                      key={o.v}
                      data-testid={`apply-income-${o.v}`}
                      onClick={() => setForm((f) => ({ ...f, has_income: o.v }))}
                      className={`py-3 rounded-xl border transition-all ${
                        form.has_income === o.v
                          ? "border-[#d4af37] bg-[rgba(212,175,55,0.12)] text-[#f0d479]"
                          : "border-[rgba(212,175,55,0.25)] text-[#dcd0b3] hover:border-[#d4af37]"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="When are you hoping to move in?">
                <select
                  data-testid="apply-input-timeline"
                  className="input-base"
                  value={form.move_in_timeline}
                  onChange={update("move_in_timeline")}
                >
                  <option value="">Select a timeline</option>
                  <option>Immediately</option>
                  <option>Within 2 weeks</option>
                  <option>Within a month</option>
                  <option>1–3 months</option>
                  <option>Just exploring</option>
                </select>
              </Field>

              <Field label="Tell us briefly about your situation">
                <textarea
                  rows={4}
                  data-testid="apply-input-message"
                  className="input-base resize-none"
                  placeholder="Share whatever feels helpful — your goals, current situation, or questions."
                  value={form.message}
                  onChange={update("message")}
                />
              </Field>

              {error && (
                <div data-testid="apply-error" className="text-red-300 text-sm bg-red-900/30 border border-red-500/40 rounded-lg p-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit-btn"
                className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>Submitting…</>
                ) : (
                  <>
                    Submit Application <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="text-xs text-[#9b87b5] text-center flex items-center justify-center gap-2 pt-2">
                <Sparkles size={12} className="text-[#d4af37]" />
                Your information is kept private and reviewed by Beds of Haven only.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm tracking-wide text-[#f0d479] mb-2 inline-block">{label}</span>
      {children}
    </label>
  );
}
